import Hash from "@/shared/helpers/Hash";

export default class API {

	constructor(baseUrl, loginUrl) {

		this.domain = baseUrl;
		this.loginUrl = loginUrl;
		this.oauthToken = null;

		this.DEBUG = false;
		this.VERSION = "/v1";
		this.ENDPOINT = this.domain + "/API" + this.VERSION;
		this.ENDPOINT_USER = this.ENDPOINT + "/user";
		this.ENDPOINT_BUSINESS = this.ENDPOINT + "/businesses/";

		this.TOKEN_SALT = "mad1tApPs!-rE7be31JA42341qqr";
		this.CONTENT_TYPE = "application/x-www-form-urlencoded;charset=UTF-8";

		this.CLIENT_ID = 'webapp';
		this.CLIENT_SECRET = 'G8N13fnrhmHN621bdhbzoWc66zCqBQ4H2a89P9a6u45bHfUSx87esurUE5';

		// GA4 session tracking
		this.ga4SessionId = null;
		this.ga4SessionNumber = null;
		this.ga4ClientId = null;

	}

	static init(baseUrl = null, loginUrl = null) {

		if (!!API.instance && baseUrl === null && loginUrl === null) {
			return API.instance;
		}
		API.instance = new API(baseUrl, loginUrl);
		API.instance.initGA4Session();
		return API.instance;

	}

	joinUrlParams(params) {

		if (params) {
			return Object.keys(params).map(key => encodeURIComponent(key) + '=' + encodeURIComponent(params[key])).join('&');
		} else {
			return "";
		}

	}

	setParams(url, data) {

		if (data && typeof data === 'object' && Object.keys(data).length > 0) {
			const params = this.joinUrlParams(data);
			url += '?' + params;
		}

		return url;

	}

	generateToken(paramsArray) {

		let joinParams = this.TOKEN_SALT + ":";
		paramsArray.forEach((param) => {
			joinParams += param;
			joinParams += "&";
		});
		joinParams = joinParams.slice(0, -1);
		return Hash.init().calculate(joinParams);

	}

	initGA4Session() {

		const storedSessionId = sessionStorage.getItem('qromo_session_id');
		const storedSessionNumber = sessionStorage.getItem('qromo_session_number');
		const storedClientId = sessionStorage.getItem('qromo_client_id');
		const storedTimestamp = sessionStorage.getItem('qromo_session_timestamp');

		const isValid = storedTimestamp && (Date.now() - parseInt(storedTimestamp)) < 10 * 60 * 1000;

		window.dataLayer = window.dataLayer || [];
		function gtag(){dataLayer.push(arguments);}
		window.gtag = gtag;

		if (storedSessionId && isValid) {
			this.ga4SessionId = storedSessionId;
			if (storedSessionNumber) {
				this.ga4SessionNumber = storedSessionNumber;
			}
			if (storedClientId) {
				this.ga4ClientId = storedClientId;
			}
		} else {

			if (typeof window.GTAG_MEASUREMENT_ID !== 'undefined' && window.GTAG_MEASUREMENT_ID) {
				this.onGtagReady(window.GTAG_MEASUREMENT_ID).then(({ clientId }) => {

					if (clientId) {
						this.ga4ClientId = clientId;
						sessionStorage.setItem('qromo_client_id', clientId);
					}

					gtag('get', window.GTAG_MEASUREMENT_ID, 'session_id', (sessionId) => {
						if (sessionId) {
							this.ga4SessionId = sessionId;
							sessionStorage.setItem('qromo_session_id', sessionId);
							sessionStorage.setItem('qromo_session_timestamp', Date.now().toString());

							gtag('get', window.GTAG_MEASUREMENT_ID, 'session_number', (sessionNumber) => {

								this.ga4SessionNumber = sessionNumber;
								sessionStorage.setItem('qromo_session_number', sessionNumber);

							});
						}
					});

				}).catch((error) => {
					console.warn('GA4 initialization failed:', error.message);
				});
			} else {
				console.log("GA4 not found");
			}
		}

	}

	onGtagReady(measurementId, { timeout = 10000 } = {}) {
		return new Promise((resolve, reject) => {
			const start = Date.now();
			(function waitForGtag() {
				if (typeof window.gtag !== 'function') {
					return Date.now() - start > timeout
						? reject(new Error('gtag() not found'))
						: setTimeout(waitForGtag, 50);
				}
				let timedOut = false;
				const t = setTimeout(() => { timedOut = true; reject(new Error('gtag get timed out')); }, timeout);

				window.gtag('get', measurementId, 'client_id', (clientId) => {
					if (!timedOut) { clearTimeout(t); resolve({ clientId }); }
				});
			})();
		});
	}

	getGA4Headers() {

		let headers = {};
		if (this.ga4SessionId !== null) {
			headers['X-GA4-SID'] = this.ga4SessionId;
		}
		if (this.ga4SessionNumber !== null) {
			headers['X-GA4-SNUM'] = this.ga4SessionNumber;
		}
		if (this.ga4ClientId !== null) {
			headers['X-GA4-CID'] = this.ga4ClientId;
		}
		return headers;

	}

	get(url, data, callback, callbackError = null, shortTimeout = false) {

		let finalUrl = this.setParams(url, data);

		let self = this;

		let fetchFunction = shortTimeout ? this.fetchWithTimeout : fetch;

		fetchFunction(finalUrl, {
			method: 'GET',
			cache: "no-store",
			credentials: 'include',
			headers: {
				"Authorization": "Bearer " + self.oauthToken,
				"Content-type": this.CONTENT_TYPE,
				...this.getGA4Headers()
			}
		})
			.then(response => {

				if (this.DEBUG) {
					return response.text();
				} else if (response.status === 401) {
					let refreshToken = localStorage.getItem("refresh_token");
					if (refreshToken) {
						API.init().loginUserRefreshToken(refreshToken, (response) => {

							if ("access_token" in response) {
								// Check for version change
								if (response.version && typeof VERSION !== 'undefined') {
									console.log("Checking version change: "+response.version+" vs "+VERSION+" ("+sessionStorage.getItem('app_version')+")");
									const currentVersion = Number(sessionStorage.getItem('app_version') || VERSION);
									const newVersion = Number(response.version);
									if (newVersion > currentVersion) {
										console.log('Version changed from '+currentVersion+' to '+newVersion+' - reloading page');
										// Add version parameter to force cache bust
										const url = new URL(window.location.href);
										url.searchParams.set('version', newVersion);
										window.location.href = url.toString();
										return;
									}
									sessionStorage.setItem('app_version', response.version);
								}
								
								API.init().oauthToken = response.access_token;
								sessionStorage.setItem("oauth_token", response.access_token);
								if (!API.init().oauthToken || API.init().oauthToken === "") {
									window.location.replace(API.init().loginUrl);
								}
								API.init().get(url, data, callback, callbackError, shortTimeout);
							} else {
								callbackError && callbackError(response);
								localStorage.removeItem("refresh_token");
								window.location.replace(API.init().loginUrl);
							}

						}, callbackError);

					} else {
						callbackError && callbackError(response);
						window.location.replace(this.loginUrl);
					}
					return null;
				} else if (response.status === 429) {
					callbackError && callbackError(response);
					alert("Please wait and retry later. Thanks!");
					return null;
				} else {
					return response.json();
				}

			})
			.then((response) => {
				if (this.DEBUG) {
					console.log('GET Fetching ' + finalUrl);
					console.log('Response: ' + response);
					callbackError && callbackError(response);
				} else if (response) {
					callback(response);
				} else {
					callbackError && callbackError(response);
				}

			})
			.catch((error) => {
				console.error('Error ('+url+'): '+JSON.stringify(error));
				callbackError && callbackError(error);
			});

	}

	fetchWithTimeout(resource, options = {}) {
		const { timeout = 8000 } = options;

		const controller = new AbortController();
		const id = setTimeout(() => {
			console.error("Fetch timed out (short)", resource);
			controller.abort();
		}, timeout);
		const response = fetch(resource, {
			...options,
			credentials: 'include',
			signal: controller.signal
		});
		return response.finally(() => {
			clearTimeout(id)
		});
	}

	post(url, data, callback, callbackError = null, shortTimeout = false, forceText = false, forceBlob = false) {

		const formBody = this.joinUrlParams(data);

		let self = this;

		let fetchFunction = shortTimeout ? this.fetchWithTimeout : fetch;

		fetchFunction(url, {
			method: 'POST',
			body: formBody,
			cache: "no-store",
			credentials: 'include',
			headers: {
				"Authorization": "Bearer " + self.oauthToken,
				"Content-type": this.CONTENT_TYPE,
				...this.getGA4Headers()
			}
		})
			.then(response => {
				if (this.DEBUG) {
					return response.text();
				} else if (response.status === 401) {

					let refreshToken = localStorage.getItem("refresh_token");
					if (refreshToken) {

						API.init().loginUserRefreshToken(refreshToken, (response) => {

							if ("access_token" in response) {
								// Check for version change
								if (response.version && typeof VERSION !== 'undefined') {
									const currentVersion = Number(sessionStorage.getItem('app_version') || VERSION);
									const newVersion = Number(response.version);
									if (newVersion > currentVersion) {
										console.log('Version changed from', currentVersion, 'to', newVersion, '- reloading page');
										// Add version parameter to force cache bust
										const url = new URL(window.location.href);
										url.searchParams.set('version', newVersion);
										window.location.href = url.toString();
										return;
									}
									sessionStorage.setItem('app_version', response.version);
								}
								
								API.init().oauthToken = response.access_token;
								sessionStorage.setItem("oauth_token", response.access_token);
								if (!API.init().oauthToken || API.init().oauthToken === "") {
									window.location.replace(API.init().loginUrl);
								}
								API.init().post(url, data, callback, callbackError);
							} else {
								callbackError && callbackError(response);
								localStorage.removeItem("refresh_token");
								window.location.replace(API.init().loginUrl);
							}

						}, callbackError);

					} else {
						callbackError && callbackError(response);
						window.location.replace(this.loginUrl);
					}
					return null;
				} else if (response.status === 429) {
					callbackError && callbackError(response);
					alert("Please wait and retry later. Thanks!");
					return null;
				} else if(forceBlob && response.status === 400) {
					return response.json();
				} else {
					if(forceText) {
						return response.text();
					} else if(forceBlob) {
						const reader = response.body.getReader();
						const chunks = [];
						let totalSize = 0;
						return reader.read().then(function process({ done, value }) {
							if (done) {
								return new Blob(chunks, { type: 'application/zip' });
							}
							chunks.push(value);
							totalSize += value.length;
							return reader.read().then(process);
						});

					} else {
						return response.json();
					}
				}
			})
			.then((response) => {
				if (this.DEBUG) {
					console.log('POST Fetching ' + url);
					console.log('Data: ' + formBody);
					console.log('Response: ' + response);
					callbackError && callbackError(response);
				} else if (response) {
					callback(response);
				} else {
					callbackError && callbackError(response);
				}
			})
			.catch((error) => {
				console.error('Error ('+url+'): '+JSON.stringify(error));
				callbackError && callbackError(error);
			});

	}

	getPublic(url, callback, callbackError = null, shortTimeout = false) {

		let fetchFunction = shortTimeout ? this.fetchWithTimeout : fetch;

		fetchFunction(url, {
			method: 'GET',
			cache: "no-store",
			credentials: 'include',
			headers: {
				...this.getGA4Headers()
			}
		})
			.then((response) => {

				if (this.DEBUG) {
					return response.text();
				} else if (response.status === 429) {
					callbackError && callbackError(response);
					alert("Please wait and retry later. Thanks!");
					return null;
				} else {
					return response.json();
				}

			})
			.then((response) => {

				if (this.DEBUG) {
					console.log('get Fetching ' + url);
					console.log('Response: ' + response);
					callbackError && callbackError(response);
				} else {
					callback(response);
				}

			})
			.catch((error) => {
				console.error('Error ('+url+'): '+JSON.stringify(error));
				callbackError && callbackError(error);
			});

	}

	postPublic(url, data, callback, callbackError = null, shortTimeout = false) {

		const formBody = this.joinUrlParams(data);
		let fetchFunction = shortTimeout ? this.fetchWithTimeout : fetch;

		fetchFunction(url, {
			method: 'POST',
			body: formBody,
			credentials: 'include',
			headers: {
				"Content-type": this.CONTENT_TYPE,
				...this.getGA4Headers()
			}
		})
			.then((response) => {

				if (this.DEBUG) {
					return response.text();
				} else {
					return response.json();
				}

			})
			.then((response) => {

				if (this.DEBUG) {
					console.log('post Fetching ' + url);
					console.log('Data: ' + formBody);
					console.log('Response: ' + response);
					callbackError && callbackError(response);
				} else {
					callback(response);
				}

			})
			.catch((error) => {
				callbackError && callbackError(error);
			});

	}

	signup(data, callback) {

		this.postPublic(this.ENDPOINT + "/signup", data, callback);

	}

	sendCustomEvent(eventName, refad, callback) {

		let timestamp = Date.now().toString();
		let token = this.generateToken([timestamp]);
		let data = {
			token: token,
			timestamp: timestamp,
			event_name: eventName,
			refad: refad
		};
		this.postPublic(this.ENDPOINT + "/custom-event", data, callback);

	}

	resetPassword(email, password, captcha, lang, callback) {

		let token = this.generateToken([email, password, captcha]);
		let data = {
			lang: lang,
			email: email,
			password: password,
			captcha: captcha,
			token: token,
			os: "webapp"
		};
		this.postPublic(this.ENDPOINT + "/reset-password", data, callback);

	}

	verifyCode(code, toUpdate, callback) {

		let token = this.generateToken([code]);
		let data = {
			token: token,
			code: code,
			to_update: toUpdate
		};
		this.postPublic(this.ENDPOINT + "/verify-code", data, callback);

	}

	verifyCodeSpeedy(code, phone, password, name, callback) {

		let token = this.generateToken([code, password]);
		let data = {
			token: token,
			code: code,
			phone: phone,
			password: password,
			name: name
		};
		this.postPublic(this.ENDPOINT + "/verify-code/speedy", data, callback);

	}

	newLead(captcha, name, email, phone, company, origin, refad, callback) {
		let token = this.generateToken([captcha, name, email, phone]);
		let data = {
			captcha: captcha,
			name: name,
			email: email,
			phone: phone,
			company: company,
			origin: origin,
			token: token
		};
		if (refad) {
			data.refad = refad;
		}
		this.postPublic(this.ENDPOINT + "/crm/webhook", data, callback);
	}

	requestInvoice(orderId, name, vat, countryCode, cf, sdi, address, callback) {
		let token = this.generateToken([name, vat, sdi]);
		let data = {
			name: name,
			vat: vat,
			country_code: countryCode,
			cf: cf,
			sdi: sdi,
			address: address,
			token: token
		};
		this.postPublic(this.ENDPOINT + "/orders/" + orderId + "/invoice", data, callback);
	}

	getBusinessSocials(businessId, callback) {

		this.getPublic(this.ENDPOINT_BUSINESS + businessId + "/public/socials", callback);

	}

	getMenuItemLists(businessId, itemId, composite, deepen, callback) {

		let data = {
			composite: composite,
			deepen: deepen
		}
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/menu/items/" + itemId, data, callback);

	}

	getMenuItemListsInfo(businessId, itemId, composite, deepen, callback) {

		let data = {
			composite: composite,
			deepen: deepen
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/info", data, callback);

	}

	getNewMenuItemExtras(businessId, itemId, callback) {

		this.getPublic(this.ENDPOINT_BUSINESS + businessId + "/public/menu/items/" + itemId + "/extras", callback);

	}

	translateMenu(businessId, languageCodeFrom, languageCodeTo, products, categories, menus, description, cover_charge_name, items, lists, callback) {

		let itemsString = JSON.stringify(items);
		let token = this.generateToken([languageCodeFrom, languageCodeTo, itemsString]);
		let data = {
			language_code_from: languageCodeFrom,
			language_code_to: languageCodeTo,
			products: JSON.stringify(products),
			categories: JSON.stringify(categories),
			menus: JSON.stringify(menus),
			description: description,
			cover_charge_name: cover_charge_name,
			items: itemsString,
			lists: JSON.stringify(lists),
			token: token
		};
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/menu/translate", data, callback);
	}

	checkAvailability(businessId, timeChecks, callback) {

		let data = {
			time_checks: JSON.stringify(timeChecks),
		};
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/menu/availability", data, callback);

	}

	translateInterface(languageCodeTo, files, keys, callback) {

		let token = this.generateToken([languageCodeTo, files]);
		let data = {
			language_code_to: languageCodeTo,
			files: files,
			keys: keys,
			token: token
		};
		this.postPublic(this.ENDPOINT + "/translate/interface", data, callback);
	}

	getPublicTakeawayTimes(businessId, callback) {

		this.getPublic(this.ENDPOINT_BUSINESS + businessId + "/public/takeaway/times", callback);

	}

	getOrdersQueue(businessId, orderId, checkOrdersQueue, checkPayBill, checkCalls, tkn, callback, callbackError) {

		let token = this.generateToken([businessId, tkn ? tkn : '-']);
		const data = {
			order_id: orderId,
			check_orders_queue: checkOrdersQueue,
			check_pay_bill: checkPayBill,
			check_calls: checkCalls,
			tkn: tkn,
			token: token
		};
		let finalUrl = this.setParams(this.ENDPOINT_BUSINESS + businessId + "/public/queue", data);
		this.getPublic(finalUrl, callback, callbackError, true);

	}

	canOrderDelivery(businessId, deliveryTimeId, callback) {

		let data = { delivery_time_id: deliveryTimeId }
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/delivery/available", data, callback);

	}

	checkoutAddEmail(businessId, email, callback) {

		let token = this.generateToken([businessId, email]);
		let data = {
			business_id: businessId,
			email: email,
			token: token,
		};
		this.postPublic(this.ENDPOINT + "/checkout/email", data, callback);

	}

	checkoutRemovePaymentMethod(businessId, paymentMethodId, callback) {

		let token = this.generateToken([businessId]);
		let data = {
			business_id: businessId,
			token: token,
			payment_method_id: paymentMethodId
		};
		this.postPublic(this.ENDPOINT + "/checkout/payment-method/delete", data, callback);

	}

	updatePageVt(businessId, menuId, roomTableId, roomId, callback = ()=>{}) {

		let token = this.generateToken([businessId]);
		let data = {
			business_id: businessId,
			menu_id: menuId,
			room_table_id: roomTableId,
			room_id: roomId,
			token: token
		}
		this.post(this.ENDPOINT + "/page/vt", data, callback);
	}

	askForAssistance(businessId, roomTableId, roomId, version, callback = ()=>{}) {

		let token = this.generateToken([businessId, roomTableId, roomId, version]);
		let tkn = typeof tknTmp !== 'undefined' ? tknTmp : null;
		let data = {
			business_id: businessId,
			room_table_id: roomTableId,
			room_id: roomId,
			token,
			tkn,
			version
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/public/assistance", data, callback);
	}

	cancelAssistance(businessId, roomTableId, roomId, version, callback = ()=>{}) {

		let token = this.generateToken([businessId, roomTableId, roomId, version]);
		let tkn = typeof tknTmp !== 'undefined' ? tknTmp : null;
		let data = {
			business_id: businessId,
			room_table_id: roomTableId,
			room_id: roomId,
			token,
			tkn,
			version
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/public/assistance/cancel", data, callback);
	}

	updatePageEvs(actions, businessId, menuId, roomTableId, sessionToken, visitToken, checkRoomTableCalls, callback = ()=>{}) {

		let token = this.generateToken([businessId, sessionToken, visitToken]);
		let data = {
			business_id: businessId,
			menu_id: menuId,
			room_table_id: roomTableId,
			check_room_table_calls: checkRoomTableCalls,
			session_token: sessionToken,
			visit_token: visitToken,
			actions: actions,
			token: token
		}
		this.post(this.ENDPOINT + "/page/evs", data, callback);
	}

	getRates(businessId, delivery_address_id, user_id, address_data, phone, name, email, products, callback) {

		let token = this.generateToken([businessId, products]);
		let data = {
			businessId: businessId,
			delivery_address_id: delivery_address_id,
			user_id: user_id,
			address_data: address_data,
			phone: phone,
			name: name,
			email: email,
			products: products,
			token: token
		};
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/rates", data, callback);

	}

	getTakeawayTimesDay(businessId, date, callback) {

		let token = this.generateToken([date]);
		let data = {
			date: date,
			token: token
		}
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/takeaway/day", data, callback);

	}

	getDeliveryTimesDay(businessId, date, callback) {

		let token = this.generateToken([date]);
		let data = {
			date: date,
			token: token
		}
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/delivery/day", data, callback);

	}

	addOrderCounter(businessId, mode, items, subtotal_delta, notes, name, email, phone, room_table_id, delivery_address_id, cover_change_num, cover_change_price, cover_paid, takeaway_costs, delivery_costs, dutch_people, status, type, paid, notInPlaceDate, amountPaidPartial, discountOnTotal, appliedCoupons, invoiceData, unlockUserId, print_single_item, callback) {

		let data = {
			mode: mode,
			items: items,
			subtotal_delta: subtotal_delta,
			notes: notes,
			name: name,
			email: email,
			phone: phone,
			room_table_id: room_table_id,
			delivery_address_id: delivery_address_id,
			cover_change_num: cover_change_num,
			cover_change_price: cover_change_price,
			cover_paid: cover_paid,
			takeaway_costs: takeaway_costs,
			delivery_costs: delivery_costs,
			dutch_people: dutch_people,
			status: status,
			type: type,
			paid: paid,
			not_in_place_date: notInPlaceDate,
			amount_paid_partial: amountPaidPartial,
			discount_on_total: discountOnTotal,
			applied_coupons: JSON.stringify(appliedCoupons),
			unlock_user_id: unlockUserId,
			invoiceData: invoiceData,
			print_single_item: print_single_item
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/counter", data, callback);

	}

	editOrder(businessId, orderId, orderMode, roomTableId, items, subtotal_delta, notes, cover_change_num, cover_change_price, takeaway_costs, delivery_costs, callback) {

		let data = {
			order_id: orderId,
			order_mode: orderMode,
			room_table_id: roomTableId,
			items: items,
			subtotal_delta: subtotal_delta,
			notes: notes,
			cover_change_num: cover_change_num,
			cover_change_price: cover_change_price,
			takeaway_costs: takeaway_costs,
			delivery_costs: delivery_costs,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/edit", data, callback);

	}

	searchMatchingCustomers(businessId, company, vat, callback) {
		let data = {
			company: company,
			vat: vat
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/counter/customers/search", data, callback);
	}

	getFilteredCustomersPeople(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/list", data, callback);

	}

	getFilteredCustomersCompanies(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/list", data, callback);

	}

	getFilteredPluginLogs(businessId, pluginId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins/" + pluginId + "/logs", data, callback);

	}

	getFilteredPersonOrders(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/orders/list", data, callback);

	}

	getFilteredPersonPayments(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/payments/list", data, callback);

	}

	getFilteredPersonOrdersAndPayments(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/orders-payments/list", data, callback);

	}

	getFilteredPersonCreditTopups(businessId, customerId, filters, callback) {

		let data = {
			filters: filters,
			customer_type: 'person'
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/credit-topups/list", data, callback);

	}

	getFilteredCompanyCreditTopups(businessId, customerId, filters, callback) {

		let data = {
			filters: filters,
			customer_type: 'company'
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/credit-topups/list", data, callback);

	}

	getBusinessCreditTopups(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/credit-topups/list", data, callback);

	}

	getPlugins(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/plugins", null, callback);

	}

	addPlugin(businessId, name, description, activeUrl, color, icon, secret, callback) {

		let data = {
			name: name,
			description: description,
			active_url: activeUrl,
			color: color,
			icon: icon,
			secret: secret
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins", data, callback);

	}

	activatePlugin(businessId, pluginId, externalLocationId, secret, callback) {

		let data = {
			external_location_id: externalLocationId,
			secret: secret
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins/" + pluginId + "/activate", data, callback);

	}

	deletePlugin(businessId, pluginId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins/" + pluginId + "/delete", data, callback);

	}

	disablePlugin(businessId, pluginId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins/" + pluginId + "/deactivate", data, callback);

	}

	syncMenu(businessId, pluginId, menuId, percentDifference, autoAssignSku, callback) {

		let data = {
			percent_difference: percentDifference,
			auto_assign_sku: autoAssignSku,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/plugins/" + pluginId + "/menus/" + menuId, data, callback);

	}

	getCustomer(businessId, customerId, customerType, callback) {

		let data = {
			customer_type: customerType
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/single", data, callback);

	}

	getCustomerFromToken(businessId, token, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/token/"+token, data, callback);

	}

	getFilteredCompanyOrders(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/orders/list", data, callback);

	}

	getFilteredCompanyPayments(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/payments/list", data, callback);

	}

	getFilteredCompanyOrdersAndPayments(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/orders-payments/list", data, callback);

	}

	getFilteredCompanyPeople(businessId, customerId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/people/list", data, callback);

	}

	getChats(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/chats", null, callback);

	}

	getChatMessages(businessId, chatId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/chat/"+chatId+"/messages", null, callback);

	}

	generateMenuPictures(businessId, menuId, categories_ids, paramsObj, callback) {
		let data = {
			categories_ids: JSON.stringify(categories_ids),
			generation_params: JSON.stringify(paramsObj)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/"+menuId+"/images/generate", data, callback);
	}

	getMenuAIImagesUpdates(businessId, callback, callbackError) {
		this.get(this.ENDPOINT_BUSINESS + businessId + "/menus/images/generating", null, callback, callbackError, true);
	}

	createChat(businessId, message, images, callback) {

		let data = {
			message,
			images: JSON.stringify(images || [])
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/chat", data, callback);

	}

	sendChatMessage(businessId, chatId, message, images, callback) {

		let data = {
			message,
			images: JSON.stringify(images || [])
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/chat/"+chatId+"/send", data, callback);

	}

	retryInvoice(businessId, invoiceId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/retry/" + invoiceId, data, callback);

	}

	emitCreditNote(businessId, invoiceId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/" + invoiceId + "/credit-note", data, callback);

	}

	switchInvoiceCustomer(businessId, invoiceId, customerId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/switch/" + invoiceId + "/" + customerId, data, callback);

	}

	printInvoice(businessId, invoiceId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/print/" + invoiceId, data, callback);

	}

	downloadInvoiceXml(businessId, invoiceId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/xml/" + invoiceId, data, callback, null, false, true);

	}

	downloadInvoicesXml(businessId, invoicesIds, callback) {

		let data = {
			invoices_ids: JSON.stringify(invoicesIds)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/xml", data, callback, null, false, false, true);

	}

	addCustomerPerson(businessId, customerId, name, email, phone, fiscal, gender, birthDate, address, notes, privacy, callback) {

		let data = {
			name: name,
			email: email,
			phone: phone,
			fiscal: fiscal,
			gender: gender,
			birth_date: birthDate,
			address: address,
			notes: notes,
			privacy: privacy,
			customer_id: customerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/add", data, callback);

	}

	updateReservation(businessId, reservationId, edits, callback) {

		let data = {
			edits: JSON.stringify(edits)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/reservations/"+reservationId+"/update", data, callback);

	}

	newReservationManual(businessId, dataReservation, callback) {

		let data = {
			data: JSON.stringify(dataReservation)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/reservations/new", data, callback);

	}

	getDayReservations(businessId, date, status, experiences, rooms, search, callback) {

		let data = {
			date: date,
			status: JSON.stringify(status),
			experiences: JSON.stringify(experiences),
			rooms: JSON.stringify(rooms),
			search: search
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/reservations/day", data, callback);

	}

	updateReservationStatus(businessId, reservationId, status, refund, callback) {

		let data = {
			status: status,
			refund: refund
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/reservations/"+reservationId+"/update/status", data, callback);

	}

	deleteCustomerPerson(businessId, customerId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/"+customerId+"/delete", data, callback);

	}

	deleteCustomerCompany(businessId, customerId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/delete", data, callback);

	}

	payCustomersOrders(businessId, orders, paymentType, invoice_data, total, customer_id, customer_type, discount_on_total, callback) {

		let data = {
			orders: JSON.stringify(orders),
			payment_type: paymentType,
			customer_id: customer_id,
			invoice_data: invoice_data,
			customer_type: customer_type,
			discount_on_total: discount_on_total,
			total: total
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/pay", data, callback);

	}

	topUpCustomerCredit(businessId, amount, customer_id, customer_type, paymentType, callback) {

		let data = {
			amount: amount,
			customer_id: customer_id,
			customer_type: customer_type,
			payment_type: paymentType
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/topup", data, callback);

	}

	payCompanyAssociatesOrders(businessId, orders, paymentType, invoice_data, total, customer_id, callback) {

		let data = {
			orders: JSON.stringify(orders),
			payment_type: paymentType,
			invoice_data: invoice_data,
			customer_id: customer_id,
			total: total
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/pay", data, callback);

	}

	getCustomersOrdersTotal(businessId, customerId, customers, callback) {

		let data = {
			customers: customers ? JSON.stringify(customers) : null,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+customerId+"/people/total", data, callback);

	}

	getCustomersAllOrders(businessId, customerId, type, callback) {

		let data = {
			type: type,
			customer_id: customerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/orders/total", data, callback);

	}

	addCustomerCompany(businessId, customerId, name, vat, countryCode, cf, sdi, address, deliveryAddress, pec, notes, privacy, callback) {

		let data = {
			name: name,
			sdi: sdi,
			vat: vat,
			country_code: countryCode,
			cf: cf,
			address: address,
			delivery_address: deliveryAddress,
			pec: pec,
			notes: notes,
			privacy: privacy,
			customer_id: customerId,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/add", data, callback);

	}

	associateCustomerToCompany(businessId, companyCustomerId, personCustomerId, callback) {

		let data = {
			person_customer_id: personCustomerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+companyCustomerId+"/associate", data, callback);

	}

	removeCustomerCompanyPerson(businessId, companyCustomerId, personCustomerId, callback) {

		let data = {
			person_customer_id: personCustomerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/companies/"+companyCustomerId+"/associate/remove", data, callback);

	}

	getMostSoldProducts(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/counter/most", null, callback);

	}

	addOrderMultiPayment(businessId, orderIds, type, amount, invoiceData, itemsSplit, dutchPeople, discountOnTotal, discount, appliedCoupons, callback) {

		let data = {
			type: type,
			amount: amount,
			order_ids: orderIds,
			items_split: itemsSplit,
			discount: discount,
			discount_on_total: discountOnTotal,
			applied_coupons: JSON.stringify(appliedCoupons),
			dutch_people: dutchPeople,
			invoice_data: invoiceData
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/payment/multi", data, callback);

	}

	checkoutInPlace(mode, businessId, captcha, captchaMode, items, notes, name, version, table, seat, cover_change_num, cover_change_price, cover_info, type_qr, email, phone, type_notification, tkn, terminalId, coupons, couponsVerificationCodes, buffering, callback) {

		let token = this.generateToken([captcha, businessId]);
		let data = {
			token: token,
			captcha: captcha,
			captcha_mode: captchaMode,
			items: items,
			notes: notes,
			name: name,
			version: version,
			table: table,
			seat: seat,
			cover_change_num: cover_change_num,
			cover_change_price: cover_change_price,
			cover_info: cover_info,
			type_qr: type_qr,
			email: email,
			phone: phone,
			type_notification: type_notification,
			tkn: tkn,
			business_id: businessId,
			terminal_id: terminalId,
			mode: mode,
			coupons: JSON.stringify(coupons),
			businesses_coupons_verification_codes: JSON.stringify(couponsVerificationCodes),
			buffering: buffering
		};
		this.postPublic(this.ENDPOINT + "/checkout/in-place", data, callback);

	}

	newReservation(businessId, data, callback) {

		data.token = this.generateToken([businessId, data.experience_id, data.date, data.time]);
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/experiences/"+data.experience_id+"/reservations", data, callback);

	}

	getExperienceAvailability(businessId, experienceId, selectedDate, callback) {

		let token = this.generateToken([businessId, experienceId]);
		let data = {
			token: token,
			selected_date: selectedDate
		}
		this.postPublic(this.ENDPOINT_BUSINESS + businessId + "/public/experiences/"+experienceId+"/availability", data, callback);

	}

	payBill(mode, businessId, amount, captcha, captchaMode, tkn, version, orderIds, typeIndex, itemsRedeemed, coupons, couponsVerificationCodes, baseAmount, callback) {

		let token = this.generateToken([captcha, businessId, mode]);
		let data = {
			token: token,
			captcha: captcha,
			captcha_mode: captchaMode,
			business_id: businessId,
			mode: mode,
			amount: amount,
			tkn: tkn,
			version: version,
			order_ids: JSON.stringify(orderIds),
			type: typeIndex,
			items_redeemed: itemsRedeemed ? JSON.stringify(itemsRedeemed) : null,
			coupons: coupons && coupons.length > 0 ? JSON.stringify(coupons) : null,
			businesses_coupons_verification_codes: couponsVerificationCodes ? JSON.stringify(couponsVerificationCodes) : null,
			base_amount: baseAmount || null,
		};
		this.postPublic(this.ENDPOINT + "/checkout/in-place/bill", data, callback);

	}

	checkoutNotInPlace(businessId, captcha, captchaMode, items, notes, typePayment, name, email, phone, type_notification, address, addressDetails, lat, lng, dataAddress, scheduledDate, rateId, orderMode, coupons, couponsVerificationCodes, callback) {

		let token = this.generateToken([captcha, businessId]);
		let data = {
			token: token,
			captcha: captcha,
			captcha_mode: captchaMode,
			items: items,
			notes: notes,
			type_payment: typePayment,
			name: name,
			email: email,
			phone: phone,
			type_notification: type_notification,
			address: address,
			address_details: addressDetails,
			lat: lat,
			lng: lng,
			data: dataAddress,
			scheduled_date: scheduledDate,
			rate_id: rateId,
			order_mode: orderMode,
			business_id: businessId,
			coupons: JSON.stringify(coupons),
			businesses_coupons_verification_codes: JSON.stringify(couponsVerificationCodes),
		};
		this.postPublic(this.ENDPOINT + "/checkout/not-in-place", data, callback);

	}


	checkoutPos(captcha, value, businessId, btc, orderImportId, callback) {

		let token = this.generateToken([captcha, value, businessId]);
		let data = {
			token: token,
			captcha: captcha,
			value: value,
			business_id: businessId,
			order_import_id: orderImportId,
			btc: btc,
			test: TEST
		};
		this.postPublic(this.ENDPOINT + "/checkout/pos", data, callback);

	}

	saveCsData(csData, callback) {

		let token = this.generateToken([csData]);
		let data = {
			token: token,
			csData: csData
		};
		this.postPublic(this.ENDPOINT + "/cs/data", data, callback);

	}

	getOrderMessages(orderId, callback) {

		this.getPublic(this.ENDPOINT + "/orders/" + orderId + "/messages", callback, null, true)

	}

	getOrderStatus(orderId, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/orders/" + orderId + "/status", callback, callbackError, true)

	}

	getPaymentStatus(paymentId, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/payments/" + paymentId + "/status", callback, callbackError, true)

	}

	getStripePaymentReceipt(paymentId, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/payments/" + paymentId + "/receipt", callback, callbackError, true)

	}

	getStripeOrderReceipt(orderId, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/orders/" + orderId + "/receipt", callback, callbackError, true)

	}

	getReservationStatus(paymentId, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/reservations/" + paymentId + "/status", callback, callbackError, true)

	}

	cancelReservation(paymentId, callback) {

		this.getPublic(this.ENDPOINT + "/reservations/" + paymentId + "/cancel", callback);

	}

	getOrderFeedback(orderId, callback) {

		this.getPublic(this.ENDPOINT + "/orders/" + orderId + "/feedback", callback)

	}

	updateFeedback(orderId, qualityRating, locationRating, serviceRating, pricingRating, feedbackDetails, feedbackId, callback) {

		let data = {
			feedback_id: feedbackId,
			quality_rating: qualityRating,
			location_rating: locationRating,
			service_rating: serviceRating,
			pricing_rating: pricingRating,
			details: feedbackDetails,
		}
		this.postPublic(this.ENDPOINT + "/orders/" + orderId + "/feedback", data, callback)

	}

	redeemItems(orderToken, businessId, items, callback, callbackError = null) {

		let token = this.generateToken([orderToken, businessId, items]);
		let data = {
			token: token,
			items: items,
			business_id: businessId
		};
		this.postPublic(this.ENDPOINT + "/orders/" + orderToken +"/redeem", data, callback, callbackError, true);

	}

	redeemVariation(orderToken, businessId, productId, itemId, callback, callbackError = null) {

		let token = this.generateToken([orderToken, businessId, productId]);
		let data = {
			token: token,
			order_item_id: itemId,
			business_id: businessId
		};
		this.postPublic(this.ENDPOINT + "/orders/" + orderToken +"/"+productId+"/redeem", data, callback, callbackError, true);

	}

	businessRedeemItems(businessId, orderId, items, callback) {

		let data = {
			items: items,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/"+orderId+"/redeem/items", data, callback);

	}

	setOrderItemPrepared(businessId, orderItemId, prepared, callback) {

		let data = {
			prepared: prepared,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/items/" + orderItemId + "/prepared", data, callback);

	}

	unsubscribeEmailUpdate(email, callback) {

		let token = this.generateToken([email]);
		let data = {
			email: email,
			token: token
		}
		this.postPublic(this.ENDPOINT + "/newsletter/unsubscribe", data, callback);

	}

	createNewsletter(businessId, prompt, images, thinkingMode, callback) {
		const data = {
			prompt
		};

		if (images && images.length > 0) {
			data.images = JSON.stringify(images);
		}

		if (thinkingMode) {
			data.thinking_mode = thinkingMode;
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/marketing/newsletter/create", data, callback);
	}

	getSocials(callback) {

		this.getPublic(this.ENDPOINT + "/socials", callback)

	}

	getThemes(callback) {

		this.getPublic(this.ENDPOINT + "/menu_themes", callback)

	}

	getAllergens(callback) {

		this.getPublic(this.ENDPOINT + "/allergens", callback)

	}

	getCountries(callback) {

		this.getPublic(this.ENDPOINT + "/countries", callback)

	}

	getLanguages(callback) {

		this.getPublic(this.ENDPOINT + "/languages", callback)

	}

	getArticles(callback) {

		this.getPublic(this.ENDPOINT + "/articles", callback)

	}

	getArticle(id, callback) {

		this.getPublic(this.ENDPOINT + "/articles/"+id, callback)

	}

	userAccount(callback) {

		this.get(this.ENDPOINT_USER + "/account", null, callback);

	}

	updateAccount(name, phone, picture, callback) {

		let data = {
			name: name,
			phone: phone,
			picture: picture
		}
		this.post(this.ENDPOINT_USER + "/account/update", data, callback);

	}

	getStripeCustomerPortal(callback) {

		this.get(this.ENDPOINT_USER + "/stripe/portal", null, callback);

	}


	copyMenu(fromBusinessId, toBusinessId, menuId, callback) {

		let data = {
			from_business_id: fromBusinessId,
			to_business_id: toBusinessId,
			menu_id: menuId
		};
		this.post(this.ENDPOINT_USER + "/businesses/menus/copy", data, callback);
	}

	userCheckoutNotInPlace(businessId, captcha, items, notes, typePayment, address_id, address, addressDetails, lat, lng, dataAddress, test, takeawayTime, rateId, callback) {

		let token = this.generateToken([captcha, businessId]);
		let data = {
			token: token,
			captcha: captcha,
			items: items,
			notes: notes,
			type_payment: typePayment,
			address_id: address_id,
			address: address,
			address_details: addressDetails,
			lat: lat,
			lng: lng,
			data: dataAddress,
			test: test,
			takeaway_date: takeawayTime,
			rate_id: rateId,
			business_id: businessId
		};
		this.post(this.ENDPOINT_USER + "/checkout/not-in-place", data, callback);

	}

	getTwoFactorAuthSecret(callback) {

		this.get(this.ENDPOINT_USER + "/2fa", null, callback);

	}

	addTwoFactorAuth(secretCode, verificationCode, callback) {

		let data = {
			secret_code: secretCode,
			verification_code: verificationCode,
		};
		this.post(this.ENDPOINT_USER + "/2fa", data, callback);

	}

	addUserAddress(address, lat, lng, addressData, callback) {

		let data = {
			address: address,
			lat: lat,
			lng: lng,
			address_data: addressData
		};
		this.post(this.ENDPOINT_USER + "/addresses", data, callback);

	}

	getUserReferralsRewards(filters, callback) {
		let data = {
			filters: JSON.stringify(filters || {})
		}
		this.post(this.ENDPOINT_USER + "/referrals", data, callback);

	}

	assignCreditToBusiness(businessId, credit, callback) {
		let data = {
			credit
		}
		this.post(this.ENDPOINT_USER + "/businesses/"+businessId+"/credits", data, callback);
	}

	getReferralGenericUrl(callback) {
		this.post(this.ENDPOINT_USER + "/referrals/link", null, callback);
	}

	emailReferralInvite(email, lang, callback) {
		let data = {
			email,
			lang
		}
		this.post(this.ENDPOINT_USER + "/referrals/invite", data, callback);
	}

	superLogin(userId, businessId, callback) {

		this.get(this.ENDPOINT_USER + "/super/login/" + userId + "/" + businessId, null, callback);

	}

	getPaymentLink(businessId, link, callback) {

		this.get(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/pay/" + link, null, callback);

	}

	getSuperInvoices(filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_USER + "/super/invoices", data, callback);

	}

	getShopOrders(filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_USER + "/super/shop-orders", data, callback);

	}

	getSuperQuotes(businessId, filters, callback) {

		let data = {
			filters: JSON.stringify(filters)
		}
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/quotes", data, callback);

	}

	getSuperQuoteDetails(quoteId, callback) {

		this.get(this.ENDPOINT_USER + "/super/quotes/" + quoteId, null, callback);

	}

	markQuoteAsPaid(quoteId, callback) {

		let data = {};
		this.post(this.ENDPOINT_USER + "/super/quotes/" + quoteId + "/mark-paid", data, callback);

	}

	markQuoteAsCanceled(quoteId, callback) {

		let data = {};
		this.post(this.ENDPOINT_USER + "/super/quotes/" + quoteId + "/mark-canceled", data, callback);

	}

	emitInvoiceAgain(businessInvoiceId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_USER + "/super/invoices/" + businessInvoiceId + "/emit", data, callback);

	}

	superEmitInvoice(businessId, paid, object, items, callback, forceIncomplete = false, quoteId = null) {
		let data = {
			paid: paid,
			object: object,
			items: items,
			business_id: businessId,
			force_incomplete: forceIncomplete
		}
		
		// Add quote_id to data if provided for update operation
		if (quoteId) {
			data.quote_id = quoteId;
		}
			
		this.post(this.ENDPOINT_USER + "/super/invoices/new", data, callback);

	}

	updateSuperQuote(quoteId, businessId, object, items, callback) {
		let data = {
			quote_id: quoteId,
			business_id: businessId,
			object: object,
			items: items,
			update_only: true // Flag to indicate this is just an update, not a new payment link
		}
		this.post(this.ENDPOINT_USER + "/super/quotes/update", data, callback);
	}

	updateQuoteTotal(quoteId, callback) {
		let data = {
			quote_id: quoteId
		}
		this.post(this.ENDPOINT_USER + "/super/quotes/update-total", data, callback);
	}

	emitInvoiceManual(businessInvoiceId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_USER + "/super/invoices/" + businessInvoiceId + "/manual", data, callback);

	}

	superEmitCreditNote(businessInvoiceId, callback) {

		let data = {}
		this.post(this.ENDPOINT_USER + "/super/invoices/" + businessInvoiceId + "/credit-note", data, callback);

	}

	getSuperLogs(filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_USER + "/super/logs", data, callback);

	}

	superGetChats(filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_USER + "/super/chats", data, callback);

	}

	superGetChat(aiChatId, callback) {

		this.get(this.ENDPOINT_USER + "/super/chats/"+aiChatId, null, callback);

	}

	superUpdateBusinessPayoutInterval(businessId, interval, callback) {
		let data = {
			interval
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/payout/interval", data, callback);
	}

	superUpdateBusinessPayoutDelay(businessId, delay, callback) {
		let data = {
			delay
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/payout/delay", data, callback);
	}

	superUpdateBusinessFeeRules(businessId, type, rules, callback) {
		let data = {
			type: type,
			rules: rules,
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/feerules", data, callback);
	}

	superReportCommissions(businessId, startDate, endDate, callback) {
		let data = {
			start_date: startDate,
			end_date: endDate
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/commissions/report", data, callback);
	}

	superStatsBusinesses(callback) {

		this.get(this.ENDPOINT_USER + "/super/stats/businesses", null, callback);

	}


	superStatsOrdersAggregated(startDate, endDate, businessIds, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
			business_ids: JSON.stringify(businessIds)
		};
		this.post(this.ENDPOINT_USER + "/super/stats/orders-aggregated", data, callback);

	}

	superStatsBusinessesAggregated(startDate, endDate, page, pageSize, sortField, sortDirection, filters, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
			page: page,
			page_size: pageSize,
			sort_field: sortField,
			sort_direction: sortDirection,
			filters: JSON.stringify(filters)
		};
		this.post(this.ENDPOINT_USER + "/super/stats/businesses-aggregated", data, callback);

	}

	superDownloadReport(startDate, endDate, businessIds, reportId, callback) {

		let data = {
			business_ids: JSON.stringify(businessIds),
			start_date: startDate,
			end_date: endDate,
			report_id: reportId
		};
		this.post(this.ENDPOINT_USER + "/super/stats/report", data, callback);

	}

	superStatsPayments(filters, callback) {

		let data = {
			filters: filters
		};
		this.post(this.ENDPOINT_USER + "/super/stats/payments", data, callback);

	}

	superUpdateBusinessType(businessId, type, callback) {
		let data = {
			type: type
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/type", data, callback);
	}

	superUpdateBusinessTransferPrefix(businessId, prefix, callback) {
		let data = {
			prefix: prefix
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/prefix", data, callback);
	}

	superUpdateTapToPayFlag(businessId, tapToPayFlag, callback) {
		let data = {
			allow_tap_to_pay: tapToPayFlag ? 1 : 0
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/tap-to-pay", data, callback);
	}

	superUpdateBusinessCredits(businessId, credits, repaymentRate, callback) {
		let data = {
			credits: credits,
			repayment_rate: repaymentRate
		};
		this.post(this.ENDPOINT_USER + "/super/businesses/" + businessId + "/credits", data, callback);
	}

	superUpdateServerResources(serverNumbers, dbSize, hoursOfTraffic, callback) {
		let data = {
			server_numbers: serverNumbers,
			db_size: dbSize,
			hours_of_traffic: hoursOfTraffic
		};
		this.post(this.ENDPOINT_USER + "/super/resources/update", data, callback);
	}

	saveMagazineArticle(articleId, articleData, callback) {

		this.post(this.ENDPOINT_USER + "/super/articles/" + articleId, articleData, callback);

	}

	deleteMagazineArticle(articleId, callback) {

		this.post(this.ENDPOINT_USER + "/super/articles/" + articleId + "/delete", {}, callback);

	}

	getBusiness(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/", null, callback);

	}

	getBusinessUsers(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/users", null, callback);

	}

	updateBusinessUsers(businessId, permissions, callback) {

		let data = {
			users: JSON.stringify(permissions)
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/users/update", data, callback);

	}

	getWarehouseItems(businessId, callback) {

		let data = {
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items", data, callback);

	}

	deleteWarehouseItem(businessId, menuItemWarehouseId, callback) {

		let data = {
			menu_item_warehouse_id: menuItemWarehouseId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items/delete", data, callback);

	}

	updateWarehouseItemSettings(businessId, menuItemWarehouseId, affectsAvailability, callback) {

		let data = {
			menu_item_warehouse_id: menuItemWarehouseId,
			affects_availability: affectsAvailability
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items/settings", data, callback);

	}

	getFilteredLogsWarehouseProduct(businessId, menuItemWarehouseId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items/"+menuItemWarehouseId+"/logs", data, callback);

	}

	getBusinessLogs(businessId, startDate, type, lastKey, callback) {

		let data = {
			start_date: startDate,
			type: type,
			last_key: lastKey
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/logs", data, callback);

	}

	addWarehouseItem(businessId, menuItemId, sku, callback) {

		let data = {
			item_id: menuItemId,
			sku: sku
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items/new", data, callback);

	}

	updateWarehouseItems(businessId, items, callback) {

		let data = {
			items: JSON.stringify(items)
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/warehouse/items/update", data, callback);

	}

	deleteBusiness(businessId, password, callback) {

		let data = {
			password: password
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delete", data, callback);

	}

	addBusinessUser(businessId, email, permissions, expires, callback) {

		let data = {
			email: email,
			permissions: JSON.stringify(permissions),
			expires: expires
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/users", data, callback);

	}

	getBusinessUnpaidInvoices(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/unpaid-invoices", {}, callback);

	}

	updateBusinessFunnelProgress(businessId, funnelStep, callback) {

		let data = {
			funnel_progress: funnelStep
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/funnel/progress", data, callback);

	}

	newBusiness(name, type, address, address_data, lat, lng, copyFromBusinessid, callback) {

		let data = {
			business_name: name,
			address: address,
			address_data: address_data,
			lat: lat,
			lng: lng,
			type: type,
			copy_from_business_id: copyFromBusinessid
		};
		this.post(this.ENDPOINT_USER + "/business", data, callback);

	}

	getAblyAuthToken(callback) {

		this.get(this.ENDPOINT_USER + "/ably/token", null, callback);

	}

	updateBusiness(businessId, data, callback) {

		Object.entries(data).forEach(([key, value]) => {
			if (value === true) {
				data[key] = 1;
			} else if (value === false) {
				data[key] = 0;
			}
		});
		this.post(this.ENDPOINT_BUSINESS + businessId + "/update", data, callback);

	}

	updateGoogleMerchantIntegration(businessId, activeGoogleMerchantFeed, callback) {

		let data = {
			google_merchant_feed_active: activeGoogleMerchantFeed
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/google-merchant-feed", data, callback);

	}

	updateByoAnalytics(businessId, analyticsGa4Id, analyticsMetaPixelId, analyticsMetaConversionsKey, analyticsTiktokPixelId, analyticsTiktokAccessToken, analyticsAdsId, analyticsAdsPurchaseLabel, callback) {

		let data = {
			analytics_ga4_id: analyticsGa4Id,
			analytics_meta_pixel_id: analyticsMetaPixelId,
			analytics_meta_conversions_key: analyticsMetaConversionsKey,
			analytics_tiktok_pixel_id: analyticsTiktokPixelId,
			analytics_tiktok_access_token: analyticsTiktokAccessToken,
			analytics_ads_id: analyticsAdsId,
			analytics_ads_purchase_label: analyticsAdsPurchaseLabel
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/byo-analytics", data, callback);

	}

	updateBusinessCutoff(businessId, code, callback) {

		let data = {
			code: code,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/update/cutoff", data, callback);

	}

	recordComingSoonInterest(businessId, type, callback) {

		let data = {
			type: type
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/coming-soon/interest", data, callback);

	}

	getOrders(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders", null, callback);

	}

	searchOrders(businessId, limit, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/limit/" + limit, null, callback);

	}

	getOrderWithId(businessId, order_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/" + order_id, null, callback);

	}

	getOrderHistory(businessId, orderId, lastKey, callback) {

		let params = {};
		if (lastKey) {
			params.last_key = lastKey;
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/" + orderId + "/history", params, callback);

	}

	getUnfiscalizedOrdersLastDay(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/today/unfiscalized", null, callback);

	}

	getEpsonReceiptData(businessId, epsonId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/epson/"+epsonId, null, callback);

	}

	logError(businessId, rawRequest, rawResponse, code, lastCommand, printerStatus, callback) {

		let data = {
			raw_request: rawRequest,
			raw_response: rawResponse,
			code: code,
			last_command: lastCommand,
			status: printerStatus
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/log", data, callback);

	}

	checkOrdersFiscal(businessId, ordersId, callback) {

		let data = {
			order_ids: JSON.stringify(ordersId)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/check", data, callback);

	}

	generateFiscalReceiptApiscontrino(businessId, ordersId, callback) {

		let data = {
			order_ids: JSON.stringify(ordersId)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/apiscontrino", data, callback);

	}

	retryFailedApiscontrinoReceipts(businessId, startDate, callback) {

		let data = {
			start_date: startDate
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/apiscontrino/retry", data, callback);

	}

	printFiscalReceiptApiscontrino(businessId, receiptId, callback) {

		let data = {
			receipt_id: receiptId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/apiscontrino/print", data, callback);

	}

	sendFiscalReceiptEmail(businessId, receiptId, email, callback) {

		let data = {
			receipt_id: receiptId,
			email: email
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/apiscontrino/email", data, callback);

	}

	getFiscalReceiptData(businessId, receiptId, callback) {

		let data = {
			receipt_id: receiptId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/apiscontrino/receipt-data", data, callback);

	}

	acceptOrder(businessId, orderId, orderAccepted, callback) {

		let data = {
			order_accepted: orderAccepted
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/" + orderId + "/accept", data, callback);

	}

	addBusinessDevice(businessId, device, token, soundIndex, callback) {

		let data = {device: device, token: token, sound: soundIndex};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/devices", data, callback);

	}

	setOrdersFiscal(businessId, ordersId, receiptData, callback) {

		let data = {
			order_ids: JSON.stringify(ordersId),
			receipt_data: receiptData
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal", data, callback);

	}

	setOrdersFiscalSent(businessId, ordersId, retrying, callback) {

		let data = {
			order_ids: JSON.stringify(ordersId),
			retrying: retrying
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/sent", data, callback);

	}

	setReceiptRefunded(businessId, receiptId, type, callback) {

		let data = {
			refund_type: type
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/"+receiptId+"/refunded", data, callback);

	}

	changeOrders(businessId, orderIds, action, checkUserHiddenCategories, callback) {

		let data = {
			order_ids: orderIds,
			action: action,
			check_user_hidden_categories: !!checkUserHiddenCategories
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/update", data, callback);

	}

	uniteOrders(businessId, orderIds, callback) {

		let data = {
			order_ids: orderIds,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/unite", data, callback);

	}

	changeOrdersPayment(businessId, orderIds, paymentType, discountOnTotal, appliedCoupons, invoiceData, printSingleItem, callback) {

		let data = {
			order_ids: orderIds,
			payment_type: paymentType,
			discount_on_total: discountOnTotal,
			applied_coupons: JSON.stringify(appliedCoupons),
			invoiceData: invoiceData,
			print_single_item: printSingleItem,
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/payment", data, callback);

	}

	addOrderMessage(businessId, orders_id, textNotice, callback) {

		let data = {
			orders_id: orders_id,
			text_notice: textNotice
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/messages", data, callback);
	}

	sendSms(businessId, orders_id, callback) {

		let data = {
			orders_id: orders_id
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/send-sms", data, callback);

	}

	getOrdersNew(businessId, roomsFilter, counterFilter, deliveryFilter, takeawayFilter, onlyMyOrders, page_size, page_offset, fiscalFilter, callback) {

		let data = {
			page_size: page_size,
			page_offset: page_offset,
			rooms_filter: roomsFilter,
			counter_filter: counterFilter,
			delivery_filter: deliveryFilter,
			takeaway_filter: takeawayFilter,
			only_my_orders: onlyMyOrders,
			fiscal_filter: fiscalFilter
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/list", data, callback);

	}

	getOrdersCompletedNew(businessId, roomsFilter, counterFilter, deliveryFilter, takeawayFilter, onlyMyOrders, page_size, page_offset, code, fiscalFilter, callback) {

		let data = {
			page_size: page_size,
			page_offset: page_offset,
			rooms_filter: roomsFilter,
			counter_filter: counterFilter,
			delivery_filter: deliveryFilter,
			takeaway_filter: takeawayFilter,
			only_my_orders: onlyMyOrders,
			code: code,
			fiscal_filter: fiscalFilter
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/list/completed", data, callback);

	}

	searchOrdersNew(businessId, search, completed, roomsFilter, counterFilter, deliveryFilter, takeawayFilter, onlyMyOrders, page_size, page_offset, code, fiscalFilter, callback) {

		let data = {
			search: search,
			completed: completed,
			page_size: page_size,
			page_offset: page_offset,
			rooms_filter: roomsFilter,
			counter_filter: counterFilter,
			delivery_filter: deliveryFilter,
			takeaway_filter: takeawayFilter,
			only_my_orders: onlyMyOrders,
			code: code,
			fiscal_filter: fiscalFilter
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/list/search", data, callback);
	}

	viewedRoomTableCall(businessId, roomTableCallId, callback) {

		let data = {};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/tables/calls/" + roomTableCallId + "/view", data, callback);
		
	}

	getFiscalUpdates(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/orders/list/fiscal", null, callback);

	}

	getRedeemedQuantities(orderToken, callback, callbackError) {

		this.getPublic(this.ENDPOINT + "/orders/" + orderToken +"/redeem", callback, callbackError, true);

	}

	createPaymentLink(productsQuantities, callback) {
		let data = {products: JSON.stringify(productsQuantities)};
		this.post(this.ENDPOINT + "/checkout/stripe/link", data, callback);
	}

	checkoutProducts(products, customerId, businessId, successUrl, failUrl, callback) {
		let data = {products: JSON.stringify(products), customer_id: customerId, business_id: businessId, success_url: successUrl, fail_url: failUrl};
		this.post(this.ENDPOINT + "/checkout/stripe/products", data, callback);
	}

	checkCoupons(businessId, coupons, cart, total, roomId, orderMode, verificationCodes, isPayBill, callback) {
		let data = {
			business_id: businessId,
			coupons: JSON.stringify(coupons),
			cart: cart,
			total: total,
			room_id: roomId,
			order_mode: orderMode,
			verification_codes: JSON.stringify(verificationCodes),
			is_paybill: isPayBill ? 1 : 0,
			token: this.generateToken([businessId, cart, orderMode])
		};
		this.post(this.ENDPOINT + "/checkout/coupons", data, callback);
	}

	statsOrders(businessId, period, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/data/orders/" + period, null, callback);

	}

	ordersDay(businessId, day, callback) {

		let data = {
			day: day
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/orders/day", data, callback);

	}

	analyticsViewsAggregated(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/views-aggregated", data, callback);

	}

	analyticsHeatMapAggregated(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/map-aggregated", data, callback);

	}

	analyticsOrders(businessId, startDate, endDate, code, canceled, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
			code: code,
			canceled: canceled
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/orders", data, callback);

	}

	analyticsOrdersAggregated(businessId, startDate, endDate, code, callback, timeGrain = null) {

		let data = {
			start_date: startDate,
			end_date: endDate,
			code: code
		}
		if (timeGrain) {
			data.time_grain = timeGrain;
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/orders-aggregated", data, callback);

	}

	getWeather(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/weather", data, callback);

	}

	getBannersData(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/data/banners", {}, callback);

	}

	analyticsOrdersReviews(businessId, startDate, endDate, page, perPage, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate,
			page: page,
			per_page: perPage
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/orders-reviews", data, callback);

	}

	analyticsProductsAggregated(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/products/aggregated", data, callback);

	}

	getProductsUsersScan(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/products/scan", data, callback);

	}

	analyticsTablesAggregated(businessId, startDate, endDate, callback) {

		let data = {
			start_date: startDate,
			end_date: endDate
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/data/tables/aggregated", data, callback);

	}

	getMenus(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menus", null, callback);

	}

	getPreSignedUrl(businessId, filename, filesize, callback) {

		let data = {
			filename: filename,
			filesize: filesize
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/image/upload", data, callback);

	}

	newMenu(businessId, name, callback) {

		let data = {
			name: name || null
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus", data, callback);

	}

	deleteMenu(businessId, menuId, callback) {

		let data = {
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/" + menuId + "/delete", data, callback);

	}

	updateMenu(businessId, menuId, name, color, picture, slug, available, counter_available, roomsExclude, callback) {

		console.log('menu name: ', name)

		let data = {
			name: name,
			color: color,
			picture: picture,
			slug: slug,
			available: available,
			counter_available: counter_available,
			rooms_exclude: roomsExclude
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/" + menuId + "/update", data, callback);

	}

	updateMenusOrder(businessId, menus, callback) {

		let data = {
			menus: JSON.stringify(menus)
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/order", data, callback);

	}

	decodeMenuFile(businessId, file, callback) {

		let data = {
			file: file
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/decode", data, callback);

	}

	getMenuResponse(businessId, aiChatResponseId, callback) {
		this.get(this.ENDPOINT_BUSINESS + businessId + "/menus/response/"+aiChatResponseId, null, callback);
	}

	getPendingMenuResponse(businessId, callback) {
		this.get(this.ENDPOINT_BUSINESS + businessId + "/menus/response/pending", null, callback);
	}

	getMenuCategories(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/categories", null, callback);

	}

	getCategoryTimes(businessId, category_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/categories/" + category_id + "/times", null, callback);

	}

	updateMenuCategories(businessId, categories, menuId, callback) {

		let data = {
			categories: categories,
			menu_id: menuId
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/categories", data, callback);

	}

	updateMenuCategoryTaxonomy(businessId, categoryId, taxonomy, callback) {

		let data = {
			taxonomy: taxonomy,
			category_id: categoryId
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/category/taxonomy", data, callback);

	}

	addCategoryTime(businessId, dayWeek, categoryId, hourFrom, hourTo, minuteFrom, minuteTo, numItems, callback) {

		let data = {
			day_week: dayWeek,
			hour_start: hourFrom,
			hour_end: hourTo,
			minute_start: minuteFrom,
			minute_end: minuteTo,
			num_items: numItems
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/categories/" + categoryId + "/times", data, callback);

	}

	copyCategoryTime(businessId, dataTimes, categoryId, callback) {

		let data = {
			data_times: dataTimes,
			category_id: categoryId
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/categories/" + categoryId + "/times/copy", data, callback);

	}

	deleteCategoryTime(businessId, categoryId, category_time_id, callback) {

		let data = {
			category_time_id: category_time_id
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/categories/" + categoryId + "/times/delete", data, callback);

	}

	getMenuItems(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/items", null, callback);

	}

	getCompositeProduct(businessId, item_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + item_id, null, callback);

	}

	getMenuItemsListsInfo(businessId, itemIds, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/lists/info/multi", {items_ids: JSON.stringify(itemIds)}, callback);

	}

	getItemTimes(businessId, item_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + item_id + "/times", null, callback);
	}

	getListInformation(businessId, itemId, list_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/lists/" + list_id, null, callback);

	}

	menuItem(businessId, item, callback) {

		let data = {
			item: item
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items", data, callback);

	}

	updateMenuItems(businessId, items, menuId, callback, returnNewItems = false) {

		let data = {
			items: items,
			menu_id: menuId
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/update", data, callback);

	}

	updateMenuItemsAvailabilities(businessId, items, callback) {

		let data = {
			items: items
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/update/availabilities", data, callback);

	}

	menuItemsDeleteImage(businessId, typeItem, itemId, callback) {

		let data = {
			type_item: typeItem
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/images/delete", data, callback);

	}

	saveListInformation(businessId, item_id, list_id, name_list, min_list, max_list, multi_list, order_list, deleted_list, products_list, callback) {

		let data = {
			list_id: list_id,
			name_list: name_list,
			min_list: min_list,
			max_list: max_list,
			multi_list: multi_list,
			order_list: order_list,
			deleted_list: deleted_list,
			products_list: products_list
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + item_id + "/lists", data, callback);

	}

	saveListsInformation(businessId, items, callback) {

		let data = {
			items: JSON.stringify(items),
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/lists", data, callback);

	}

	updateMenuItemLists(businessId, menuItemId, lists, callback) {

		let data = {
			lists: JSON.stringify(lists),
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/"+menuItemId+"/lists/update", data, callback);

	}

	addItemTime(businessId, dayWeek, itemId, hourFrom, hourTo, minuteFrom, minuteTo, numItems, callback) {

		let data = {
			day_week: dayWeek,
			hour_start: hourFrom,
			hour_end: hourTo,
			minute_start: minuteFrom,
			minute_end: minuteTo,
			num_items: numItems
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/times", data, callback);

	}

	copyItemTime(businessId, dataTimes, itemId, callback) {

		let data = {
			data_times: dataTimes
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/times/copy", data, callback);

	}

	deleteItemTime(businessId, itemId, item_time_id, callback) {

		let data = {
			item_time_id: item_time_id
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/times/delete", data, callback);

	}

	updateItemTimes(businessId, itemId, times, callback) {

		let data = {
			data_times: times
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/" + itemId + "/times/update", data, callback);

	}

	updateDailyReportTimes(businessId, times, callback) {

		let data = {
			times: times
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/report/slots", data, callback);

	}

	getDailyReportTimeSlots(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/settings/report/slots", null, callback);

	}

	copyItemsTimes(businessId, items, callback) {

		let data = {
			items: JSON.stringify(items)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/items/times/copy", data, callback);

	}

	updateCategoryTimes(businessId, categoryId, times, callback) {

		let data = {
			data_times: times
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menu/categories/" + categoryId + "/times/update", data, callback);

	}

	updateDeliveryTimes(businessId, times, callback) {

		let data = {
			data_times: times
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/times/update", data, callback);

	}

	updateTakeawayTimes(businessId, times, callback) {

		let data = {
			data_times: times
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/takeaway/times/update", data, callback);

	}

	getRooms(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/rooms", null, callback);

	}

	getRoomStatus(businessId, roomId, reservations, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/rooms/" + roomId + "?reservations=" + reservations, null, callback);

	}

	addRoom(businessId, name, acceptOrders, enabledCalls, elements, callback) {

		let data = {
			name: name,
			accept_orders: acceptOrders,
			enabled_calls: enabledCalls,
			elements: elements
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms", data, callback);

	}

	addRoomElements(businessId, roomId, elements, callback) {

		let data = {
			elements: elements
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/" + roomId + "/elements/add", data, callback);

	}

	updateRoom(businessId, roomId, name, acceptOrders, preventOrderCompletion, enabledSms, enabledCalls, cover, enablePayBill, forcePayBill, notificationSound, callback) {

		let data = {
			name: name,
			accept_orders: acceptOrders,
			prevent_order_completion: preventOrderCompletion,
			enabled_sms: enabledSms,
			enabled_calls: enabledCalls,
			enable_pay_bill: enablePayBill,
			cover_charge: cover?.cover_charge,
			cover_charge_type: cover?.cover_charge_type,
			cover_charge_name: cover?.cover_charge_name,
			cover_charge_everytime: cover?.cover_charge_everytime,
			force_pay_bill: forcePayBill,
			notification_sound: notificationSound,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/"+roomId+"/update", data, callback);

	}

	deleteRoom(businessId, roomId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/"+roomId+"/delete", data, callback);

	}

	getCoupons(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/coupons", null, callback);

	}

	deleteCoupon(businessId, couponId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/coupons/"+couponId+"/delete", null, callback);

	}

	addCoupon(businessId, name, code, fixedValue, percentValue, maxRedeems, minOrderValue, canStack, oncePerUser, firstTimeOnly, qrMode, paytableMode, delivery, takeaway, linkedProducts, linkedCategories, linkedRooms, expireDate, verificationCodes, callback) {

		let data = {
			name: name,
			code: code,
			fixed_value: fixedValue,
			percentage_value: percentValue,
			max_redeems: maxRedeems,
			minimum_order_value: minOrderValue,
			can_stack: canStack,
			once_per_user: oncePerUser,
			first_time_only: firstTimeOnly,
			qr_mode: qrMode,
			paytable_mode: paytableMode,
			delivery: delivery,
			takeaway: takeaway,
			linked_products: linkedProducts,
			linked_categories: linkedCategories,
			linked_rooms: linkedRooms,
			date_expire: expireDate,
			verification_codes: JSON.stringify(verificationCodes || [])
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/coupons", data, callback);

	}

	getCouponVerificationCodes(businessId, businessCouponId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/coupons-verification-codes/" + businessCouponId, null, callback);
	}

	updateCouponVerificationCodes(businessId, businessCouponId, verificationCodes, callback) {

		let data = {
			verification_codes: JSON.stringify(verificationCodes || [])
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/coupons-verification-codes/" + businessCouponId, data, callback);

	}

	updateRoomType(businessId, roomId, type, callback) {

		let data = {
			type: type
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/" + roomId + "/type", data, callback);

	}

	saveRoomTables(businessId, roomId, elements, callback) {

		let data = {
			elements: elements
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/" + roomId + "/elements", data, callback);

	}

	deleteRoomTable(businessId, roomTableId, callback) {

		let data = {
			room_table_id: roomTableId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/tables/remove", data, callback);

	}

	requestQrPrint(businessId, type, quantity, callback) {

		let data = {
			type: type,
			quantity: quantity
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/rooms/tables/request", data, callback);

	}

	connectStripe(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/connect", null, callback);

	}

	migrateStripeToCustom(businessId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/migrate", null, callback);

	}

	getStripeConnectSession(businessId, sessionType, callback) {
		const data = sessionType ? { session_type: sessionType } : null;
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/session", data, callback);
	}

	getFilteredStripeBalance(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/balance/info", data, callback);

	}

	getReconcileTransactionsTransfer(businessId, transferId, callback) {

		let data = {
			transfer_id: transferId,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/transactions", data, callback);
	}

	getFilteredInvoices(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices", data, callback);

	}

	getFilteredEpsonReceipts(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/receipts/epson", data, callback);

	}

	getFilteredEpsonZReports(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/receipts/epson/z-reports", data, callback);

	}

	getFilteredApiscontrinoReceipts(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/receipts/apiscontrino", data, callback);

	}

	getFilteredBusinessToCustomerInvoices(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/invoices/b2c", data, callback);

	}

	getBusinessFeedbacks(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/feedbacks", null, callback);

	}

	getStripeExpressDashboardUrl(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/express/dashboard", null, callback);

	}

	getStripeIdentityVerificationSession(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/identity/verification", null, callback);

	}

	updateBusinessPayoutDelay(businessId, newDelay, callback) {

		let data = {
			payout_delay: newDelay
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/payout/delay", data, callback);

	}

	checkBalance(businessId, callback) {
		this.get(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/payout/check-balance", {}, callback);
	}

	instantPayout(businessId, amount, destination, callback) {
		let data = {
			amount: amount,
			destination: destination
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/payout/instant", data, callback);
	}

	getFilteredStripeTransactions(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/transactions/info", data, callback);

	}

	getFilteredBalanceChanges(businessId, filters, callback) {

		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/balance/history", data, callback);

	}

	refundStripeTransaction(businessId, transactionId, amount, reason, callback) {

		let data = {
			amount: amount || null,
			reason: reason || null,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/transaction/"+transactionId+"/refund", data, callback);

	}

	sendTransactionReceiptEmail(businessId, email, transactionId, callback) {

		let data = {
			email: email
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/stripe/transaction/"+transactionId+"/receipt", data, callback);

	}

	satispayCreate(businessId, active, satispay_token, callback) {

		let data = {
			satispay_token: satispay_token,
			active: active
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/satispay/create", data, callback);

	}

	voucherlyCreate(businessId, active, apiKey, callback) {

		let data = {
			api_key: apiKey,
			active
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/voucherly/create", data, callback);

	}

	btcpayCreate(businessId, password, callback) {

		let data = {
			password: password
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/payments/btc/create", data, callback);

	}

	btcpayInvoiceTest(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/payments/btc/invoice/test", null, callback);

	}

	stripeSubscriptionMetered(businessId, dataAction, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/buy/metered/" + dataAction, null, callback);

	}

	stripeUploadMenuService(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/buy/menu-service", null, callback);

	}

	stripeBuyIssuingCards(businessId, type, callback) {

		let data = {
			type: type
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/issuing/cards", data, callback);

	}

	stripeRechargeCredit(businessId, amount, autoRecharge, callback) {

		let data = {
			amount: amount,
			auto_recharge: autoRecharge
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/recharge/create-checkout-session", data, callback);

	}

	stripeSubscribeToPlan(businessId, planName, billingCycle, callback) {

		let data = {
			plan_name: planName,
			billing_cycle: billingCycle
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/subscription", data, callback);

	}

	stripeSubscriptionUpdate(businessId, newSubName, changeCycle, callback) {

		let data = {
			new_sub: newSubName,
			change_cycle: changeCycle ? 1 : 0
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/update-plan", data, callback);

	}

	stripeBuyPlugins(businessId, orderPay, delivery, takeaway, pos, counter, payBill, callback) {

		let data = {
			plan_plugin_order_pay: orderPay,
			plan_plugin_delivery: delivery,
			plan_plugin_takeaway: takeaway,
			plan_plugin_pos: pos,
			plan_plugin_counter: counter,
			plan_plugin_pay_bill: payBill
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/plugins", data, callback);

	}

	stripeToggleExternalPlugins(businessId, pluginId, enable, callback) {

		let data = {
			external_plugin_id: pluginId,
			enable: !!enable
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/plugins/external", data, callback);

	}

	stripeSubscriptionCycleUpdate(businessId, planCycle, callback) {

		let data = {
			plan_cycle: planCycle
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/buy/cycle", data, callback);

	}

	createLink(businessId, name, callback) {

		let data = {
			name: name
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/link", data, callback);

	}

	updateBusinessManualCode(businessId, code, callback) {

		let data = {
			code: code
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/manual/code", data, callback);

	}

	updatePaymentMethods(businessId, accept_cash, accept_cards, accept_bitcoin, accept_satispay, accept_klarna, accept_voucherly, callback) {

		let data = {
			accept_cash,
			accept_cards,
			accept_bitcoin,
			accept_satispay,
			accept_klarna,
			accept_voucherly
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/payment-methods", data, callback);

	}

	updateHidePrices(businessId, hide_prices, callback) {

		let data = {
			hide_prices: hide_prices
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/hide-prices", data, callback);

	}

	updateIntegration(businessId, integration, callback) {

		let data = {
			integration: integration
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/integration", data, callback);

	}

	updateBusinessDelivery(businessId, delivery_active, callback) {

		let data = {
			delivery_active: delivery_active
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/delivery", data, callback);

	}

	addNewAddress(businessId, address, lat, lng, addressData, insertType, callback) {

		let data = {
			address: address,
			lat: lat,
			lng: lng,
			address_data: addressData,
			insert_type: insertType
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/address", data, callback);

	}

	saveSocialUrl(businessId, social, callback) {

		let data = {social: social}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/socials", data, callback);

	}

	getDeliveryTimes(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/delivery/times", null, callback);

	}

	updateDeliveryCash(businessId, delivery_cash, callback) {

		let data = {
			delivery_cash: delivery_cash,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/cash", data, callback);

	}

	addDeliveryTime(businessId, dayWeek, hourFrom, hourTo, minuteFrom, minuteTo, numOrders, callback) {

		let data = {
			day_week: dayWeek,
			hour_start: hourFrom,
			hour_end: hourTo,
			minute_start: minuteFrom,
			minute_end: minuteTo,
			num_orders: numOrders
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/times", data, callback);

	}

	getDeliveryCouriers(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/delivery/couriers", null, callback);

	}

	updateDeliveryCouriers(businessId, couriers, callback) {

		let data = {
			couriers: JSON.stringify(couriers)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/couriers", data, callback);

	}

	copyDeliveryTime(businessId, dataTimes, callback) {

		let data = {
			data_times: dataTimes
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/times/copy", data, callback);

	}

	deleteDeliveryTime(businessId, delivery_time_id, callback) {

		let data = {
			delivery_time_id: delivery_time_id
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/delivery/times/delete", data, callback);

	}

	getTakeawayTimes(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/takeaway/times", null, callback);

	}

	updateTakeawayOtherPayments(businessId, props, callback) {

		let data = {
			takeaway_cash: props?.takeaway_cash ? 1 : 0,
			takeaway_enabled_sms: props?.takeaway_enabled_sms ? 1 : 0,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/takeaway/other-payments", data, callback);
	}

	addTakeawayTime(businessId, dayWeek, hourFrom, hourTo, minuteFrom, minuteTo, numOrders, callback) {

		let data = {
			day_week: dayWeek,
			hour_start: hourFrom,
			hour_end: hourTo,
			minute_start: minuteFrom,
			minute_end: minuteTo,
			num_orders: numOrders
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/takeaway/times", data, callback);

	}

	copyTakeawayTime(businessId, dataTimes, callback) {

		let data = {
			data_times: dataTimes
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/takeaway/times/copy", data, callback);

	}

	deleteTakeawayTime(businessId, takeaway_time_id, callback) {

		let data = {
			takeaway_time_id: takeaway_time_id
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/takeaway/times/delete", data, callback);

	}

	getKitchenStations(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/kitchen", null, callback);

	}

	getKitchenStationExcludedCategories(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/kitchen/categories", null, callback);

	}

	kitchenStationUpdateExcludedCategories(businessId, id, categories, callback) {

		let data = {
			categories: categories
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/kitchen/"+id+"/categories", data, callback);

	}

	getPrinters(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/printers", null, callback);

	}

	getNewsletters(businessId, statusFilters, callback) {

		const payload = statusFilters ? { status_filters: JSON.stringify(statusFilters) } : null;
		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters", payload, callback);

	}

	getNewsletter(businessId, newsletterId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId, null, callback);

	}

	getNewsletterConversation(businessId, newsletterId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/conversation", null, callback);

	}

	getNewsletterStats(businessId, newsletterId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/stats", null, callback);

	}

	getFilteredNewslettersStats(businessId, filters, callback) {
		let data = {
			filters: filters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/stats", data, callback);
	}

	editNewsletter(businessId, newsletterId, prompt, sectionId, images, thinkingMode, callback) {

		const data = {
			prompt: prompt
		};

		if (sectionId) {
			data.section_id = sectionId;
		}

		if (images && images.length > 0) {
			data.images = JSON.stringify(images);
		}

		if (thinkingMode) {
			data.thinking_mode = thinkingMode;
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/edit", data, callback);

	}

	rollbackNewsletter(businessId, newsletterId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/rollback", {}, callback);

	}

	sendTestNewsletter(businessId, newsletterId, testEmail, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/send-test", {
			email: testEmail
		}, callback);

	}

	getNewsletterRecipientsCount(businessId, newsletterId, sections, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/recipients/count?sections=" + encodeURIComponent(JSON.stringify(sections)), null, callback);

	}

	sendNewsletter(businessId, newsletterId, sections, scheduledAt, callback) {

		const data = {
			sections: JSON.stringify(sections)
		};

		if (scheduledAt) {
			data.scheduled_at = scheduledAt.toISOString();
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/send", data, callback);

	}

	cancelScheduledNewsletter(businessId, newsletterId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/cancel-schedule", {}, callback);

	}

	updateNewsletterSubject(businessId, newsletterId, subject, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/subject", {
			subject: subject
		}, callback);

	}

	duplicateNewsletter(businessId, newsletterId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/duplicate", {}, callback);

	}

	deleteNewsletter(businessId, newsletterId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/newsletters/" + newsletterId + "/delete", {}, callback);

	}

	claimMarketingWelcomeBonus(businessId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/marketing/claim-welcome-bonus", {}, callback);

	}

	getCards(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/cards", null, callback);

	}

	getCardsEphemeralKey(businessId, cardId, nonce, otpCode, callback) {

		let data = {
			otp_code: otpCode
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/"+cardId+"/nonce/"+nonce, data, callback);

	}

	updateCard(businessId, cardId, data, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/"+cardId+"/update", data, callback);

	}

	newCard(businessId, name, cardholderId, type, color, address, callback) {

		let data = {
			name: name,
			cardholder_id: cardholderId,
			type: type,
			color: color,
			address: address
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/new", data, callback);

	}

	freezeCard(businessId, cardId, active, callback) {

		let data = {
			active: active
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/"+cardId+"/freeze", data, callback);

	}

	cancelCard(businessId, cardId, otpCode, callback) {

		let data = {
			otp_code: otpCode
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/"+cardId+"/cancel", data, callback);

	}

	newCardholder(businessId, name, surname, email, phone, address, birthDate, callback) {

		let data = {
			name: name,
			surname: surname,
			email: email,
			phone: phone,
			birth_date: birthDate,
			address: address
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cardholders", data, callback);

	}

	requestIssuingCapability(businessId, callback) {
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/request-issuing", {}, callback);
	}

	getCardSpendingControls(businessId, cardId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/cards/" + cardId + "/spending-controls", null, callback);

	}

	getCardTransactions(businessId, cardId, startDate, callback) {

		let data = {
			start_date: startDate
		};
		
		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/" + cardId + "/transactions", data, callback);

	}

	getFilteredCardTransactions(businessId, cardId, filters, callback) {

		const endpoint = cardId !== null
			? this.ENDPOINT_BUSINESS + businessId + "/cards/" + cardId + "/transactions/filtered"
			: this.ENDPOINT_BUSINESS + businessId + "/cards/transactions/filtered";

		let data = {
			filters: JSON.stringify(filters)
		}
		this.post(endpoint, data, callback);

	}

	getFundingInstructions(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/cards/funding-instructions", null, callback);

	}

	getIssuingBalance(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/cards/balance", null, callback);

	}
	
	createIssuingPayout(businessId, amount, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/payout", { amount: amount }, callback);

	}
	
	updateAutoRechargeSettings(businessId, data, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/auto-recharge", data, callback);

	}

	simulateTestTransfer(businessId, amount, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/simulate-test-transfer", { amount: amount }, callback);

	}

	simulateCardAuthorization(businessId, cardId, amount, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/cards/" + cardId + "/simulate-authorization", { amount: amount }, callback);

	}


	getTerminals(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/terminals", null, callback);

	}

	updateTerminals(businessId, newTerminals, callback) {

		let data = { terminals: newTerminals }
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals", data, callback);

	}

	updatePaxTerminals(businessId, newTerminals, callback) {

		let data = { terminals: newTerminals }
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/pax", data, callback);

	}

	updateDojoTerminals(businessId, newTerminals, callback) {

		let data = { terminals: newTerminals }
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/dojo", data, callback);

	}

	printTest(businessId, printer_id, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/printers/" + printer_id + "/test", null, callback);

	}

	updateTerminalConfiguration(businessId, terminalId, configuration, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + `/terminals/${terminalId}/configuration`, configuration, callback);

	}

	printerChangeCategory(businessId, category_id, categories, callback) {

		let data = {
			category_id: category_id,
			categories: categories
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/categories", data, callback);

	}

	printerUpdateExcludedCategories(businessId, id, categories, categoriesSmall, users, callback) {

		let data = {
			categories: categories,
			categoriesSmall: categoriesSmall,
			users: users
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printer/"+id+"/categories", data, callback);

	}

	printerUpdate(
		businessId,
		printer_id,
		name,
		active,
		print_order_total,
		name_break_line,
		product_double_height,
		current_course_big,
		white_space_header,
		left_side_quantities,
		header_double_size,
	    print_redeem_qr,
		print_order_qr,
		print_exploded_items,
		print_staff_name,
		edit_order_summary,
		can_reprint_orders,
		can_print_prebill,
		can_print_posreceipt,
		can_print_reservation,
		can_print_customer_credit,
		play_audio,
		fiscal_print,
		mode_counter_business,
		mode_qr_business,
		mode_delivery_business,
		mode_takeaway_business,
		mode_qr_customer,
		mode_delivery_customer,
		mode_takeaway_customer,
		callback
	) {

		let data = {
			id: printer_id,
			name: name,
			active: active,
			print_order_total: print_order_total,
			name_break_line: name_break_line,
			can_reprint_orders: can_reprint_orders,
			can_print_prebill: can_print_prebill,
			can_print_posreceipt: can_print_posreceipt,
			can_print_reservation: can_print_reservation,
			can_print_customer_credit: can_print_customer_credit,
			product_double_height: product_double_height,
			current_course_big: current_course_big,
			white_space_header: white_space_header,
			left_side_quantities: left_side_quantities,
			header_double_size: header_double_size,
			print_redeem_qr: print_redeem_qr,
			print_order_qr: print_order_qr,
			print_exploded_items: print_exploded_items,
			print_staff_name: print_staff_name,
			edit_order_summary: edit_order_summary,
			play_audio: play_audio,
			fiscal_print: fiscal_print,
			mode_counter_business: mode_counter_business,
			mode_qr_business: mode_qr_business,
			mode_delivery_business: mode_delivery_business,
			mode_takeaway_business: mode_takeaway_business,
			mode_qr_customer: mode_qr_customer,
			mode_delivery_customer: mode_delivery_customer,
			mode_takeaway_customer: mode_takeaway_customer,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/update", data, callback);

	}

	deletePrinter(businessId, printerId, callback) {

		let data = {
			printer_id: printerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/delete", data, callback);

	}

	getExperiences(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/experiences", null, callback);

	}

	getExperienceSchedule(businessId, experienceId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/experiences/" + experienceId + "/schedule", null, callback);

	}

	getExperienceExceptions(businessId, experienceId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/experiences/" + experienceId + "/exceptions", null, callback);

	}

	saveExperienceExceptions(businessId, experienceId, exceptions, callback) {

		let data = {
			exceptions: exceptions
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/experiences/" + experienceId + "/exceptions", data, callback);

	}

	saveExperiences(businessId, experiences, callback) {

		let data = {
			experiences: experiences
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/experiences", data, callback);

	}

	saveExperienceTables(businessId, experienceId, tables, callback) {

		let data = {
			tables: tables
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/experiences/"+experienceId+"/tables", data, callback);

	}

	saveExperienceSchedule(businessId, experienceId, timeslots, callback) {

		let data = {
			timeslots: timeslots,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/experiences/"+experienceId+"/schedule", data, callback);

	}

	clonePrinter(businessId, printerId, callback) {

		let data = {
			printer_id: printerId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/clone", data, callback);

	}

	registerNewPrinter(businessId, sn, callback) {

		let data = {
			sn: sn
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/new", data, callback);

	}

	printAggregateReceipt(businessId, ordersId, callback) {

		let data = {
			orders_id: ordersId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/aggregate", data, callback);

	}

	printReport(businessId, rows, title, subtitle, appliedFilters, callback) {

		let data = {
			rows: rows,
			title: title,
			subtitle: subtitle,
			applied_filters: appliedFilters
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/report", data, callback);

	}

	printPrebill(businessId, ordersId, allItems, callback) {

		let data = {
			orders_id: ordersId,
			all_items: allItems
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/prebill", data, callback);

	}

	printPosReceipts(businessId, id, callback) {

		let data = {
			id: id,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/pos", data, callback);

	}

	getPosReceiptData(businessId, id, callback) {

		let data = {
			id: id,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/pos/data", data, callback);

	}

	printReservation(businessId, reservationId, callback) {

		let data = {
			reservation_id: reservationId,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/reservation", data, callback);

	}

	printCustomerCreditQr(businessId, customerId, customerType, callback) {

		let data = {
			customer_id: customerId,
			customer_type: customerType,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/customer-credit-qr", data, callback);

	}

	printReservationsDay(businessId, selectedDate, filters, callback, callbackError = null) {
		let data = {
			selectedDate: selectedDate,
			filters: JSON.stringify(filters)
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/reservations/date", data, callback, callbackError);
	}

	payTerminal(businessId, terminal_id, amount, noStats, orderIds, chosenPaymentMode, itemsRedeemed, dutchPeople, invoiceData, discountOnTotal, discount, appliedCoupons, skipTipping, printSingleItem, callback, callbackError) {

		let data = {
			amount: amount,
			no_stats: noStats,
			chosen_payment_mode: chosenPaymentMode,
			order_ids: JSON.stringify(orderIds),
			items_redeemed: JSON.stringify(itemsRedeemed),
			dutch_people: JSON.stringify(dutchPeople),
			invoice_data: JSON.stringify(invoiceData),
			discount_on_total: discountOnTotal,
			discount: discount,
			applied_coupons: JSON.stringify(appliedCoupons),
			skipTipping: skipTipping,
			print_single_item: printSingleItem
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/"+terminal_id+"/pay", data, callback, callbackError);

	}

	payTerminalLocal(businessId, amount, noStats, notes, orderIds, type, chosenPaymentMode, items_redeemed, dutchPeople, invoiceData, discountOnTotal, discountPartial, appliedCoupons, printSingleItem, callback) {

		let data = {
			amount: amount,
			no_stats: noStats,
			notes: notes,
			order_ids: JSON.stringify(orderIds),
			items_redeemed: JSON.stringify(items_redeemed),
			dutch_people: JSON.stringify(dutchPeople),
			invoice_data: JSON.stringify(invoiceData),
			type: type,
			discount_on_total: discountOnTotal,
			discount: discountPartial,
			applied_coupons: JSON.stringify(appliedCoupons),
			chosen_payment_mode: chosenPaymentMode,
			print_single_item: printSingleItem,
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/pay/local", data, callback);

	}

	cancelTerminalPayment(businessId, terminal_id, pos_order_id, callback) {

		let data = {
			pos_order_id: pos_order_id
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/"+terminal_id+"/cancel", data, callback);

	}

	deleteTerminal(businessId, terminal_id, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/"+terminal_id+"/delete", data, callback);

	}

	deleteTerminalPax(businessId, terminal_id, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/pax/"+terminal_id+"/delete", data, callback);

	}

	deleteTerminalDojo(businessId, terminal_id, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/terminals/dojo/"+terminal_id+"/delete", data, callback);

	}

	addNewWebhook(businessId, name, url, types, callback) {

		let data = {
			name: name,
			url: url,
			types: JSON.stringify(types),
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/webhooks", data, callback);

	}

	updateWebhookTypes(businessId, webhookId, types, callback) {

		let data = {
			business_webhook_id: webhookId,
			types: JSON.stringify(types)
		};
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/webhooks/update", data, callback);

	}

	getWebhookLog(businessId, webhookId, filters, callback) {

		let data = JSON.stringify(filters);
		this.post(this.ENDPOINT_BUSINESS + businessId + '/settings/webhooks/' + webhookId + '/logs', {filters: data}, callback);

	}

	deleteWebhook(businessId, webhookId, callback) {

		let data = {}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/webhooks/"+webhookId+"/delete", data, callback);

	}

	getPosOrder(businessId, terminal_id, order_id, callback, callbackError) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/terminals/"+terminal_id+"/orders/"+order_id, null, callback, callbackError, true);

	}

	getBusinessLocation(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/terminals/location/default", null, callback);

	}

	printFiscalReceipt(businessId, orderId, callback) {

		let data = {
			order_id: orderId
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/printers/print/fiscal", data, callback);

	}

	connectAde(businessId, adeLogin, adePassword, adePin, delegated, callback) {

		let data = {
			ade_login: adeLogin,
			ade_password: adePassword,
			ade_pin: adePin,
			delegated: delegated
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/apiscontrino/connect", data, callback);

	}

	disconnectAde(businessId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/apiscontrino/disconnect", {}, callback);

	}

	connectApifattura(businessId, prefixNumeration, sendSdi, callback) {

		let data = {
			prefix_numeration: prefixNumeration,
			send_sdi: sendSdi
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/apifattura/connect", data, callback);

	}

	disconnectApifattura(businessId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/apifattura/disconnect", {}, callback);

	}

	integrationDojoConnect(businessId, dojoApiKey, callback) {

		let data = {
			dojo_api_key: dojoApiKey
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/dojo/connect", data, callback);

	}

	integrationDojoDisconnect(businessId, callback) {

		this.post(this.ENDPOINT_BUSINESS + businessId + "/integrations/dojo/disconnect", {}, callback);

	}

	getPublicCompanyInfoFromVat(vat, countryCode, orderId, callback) {

		if(!vat || !vat?.length || vat?.length !== 11 || !orderId) {
			callback(null);
		} else {
			let data = {
				vat: vat,
				country_code: countryCode
			}
			this.post(this.ENDPOINT + "/orders/"+orderId+"/vat", data, callback);
		}

	}

	getBusinessCompanyInfoFromVat(vat, countryCode, businessId, callback) {

		if(!vat || !vat?.length || vat?.length !== 11 || !businessId) {
			callback(null);
		} else {
			let data = {
				vat: vat,
				country_code: countryCode
			}
			this.post(this.ENDPOINT_BUSINESS + businessId + "/customers/vat", data, callback);
		}

	}

	loginUser(email, password, otp, callback) {

		let data = {
			grant_type: 'password',
			client_secret: this.CLIENT_SECRET,
			client_id: this.CLIENT_ID,
			username: email,
			password: password,
			otp: otp
		};
		this.postPublic(this.ENDPOINT + "/oauth/token", data, callback);

	}

	loginUserRefreshToken(tkn, callback, callbackError = null) {

		let data = {
			grant_type: 'refresh_token',
			client_secret: this.CLIENT_SECRET,
			client_id: this.CLIENT_ID,
			refresh_token: tkn
		};
		this.postPublic(this.ENDPOINT + "/oauth/token", data, callback, callbackError);

	}

	logout(callback) {

		let data = {};
		this.post(this.ENDPOINT_USER + "/logout", data, callback);

	}

	getBusinessShortcuts(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/settings/shortcuts", null, callback);

	}

	getBusinessApiscontrinoExcludeUsers(businessId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/settings/apiscontrino/users", null, callback);

	}

	updateBusinessShortcuts(businessId, data, callback) {

		let obj = {
			'shortcuts': data
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/shortcuts", obj, callback);

	}

	updateBusinessApiscontrinoExcludeUsers(businessId, data, callback) {

		let obj = {
			'excluded_users': data
		}

		this.post(this.ENDPOINT_BUSINESS + businessId + "/settings/apiscontrino/users", obj, callback);

	}

	getMenuRoomsExcluded(businessId, menuId, callback) {

		this.get(this.ENDPOINT_BUSINESS + businessId + "/menus/" + menuId + "/rooms/excluded", null, callback);

	}

	updateMenuRoomsExcluded(businessId, menuId, rooms, callback) {

		let data = {
			rooms: rooms
		}
		this.post(this.ENDPOINT_BUSINESS + businessId + "/menus/" + menuId + "/rooms/excluded", data, callback);

	}

	zReportResultIngest(businessId, data, callback) {

		let body = {...data};
		body.breakdown = JSON.stringify(body.breakdown);
		this.post(this.ENDPOINT_BUSINESS + businessId + "/orders/fiscal/epson/z-report", body, callback);

	}

}
